import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TitleCasePipe } from '@angular/common';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { ApiLavandierService } from '../../../../lib/lib-ngx/web-services/api-lavandier.service';
import { IArticlePackPostReq } from '../../../../lib/lib-shared/api/lavandier/ArticlePack.Type';
import { IUser } from '../../../../lib/lib-shared/models/User';
import { PRICEMULTIPLIER } from '../../../../lib/lib-shared/defines';

@Component({
  selector: 'lm-article-pack-edit',
  templateUrl: './article-pack-edit.component.html',
  styleUrls: ['./article-pack-edit.component.scss']
})
export class ArticlePackEditComponent implements OnInit {
  public articlePackId: number = null;
  public articleList: any[] = [];
  public userList: IUser[] = [];

  public articlePackForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    discount: [null, Validators.required],
    all: [false],
    articleList: this.fb.array([]),
  });

  constructor(
    private fb: FormBuilder,
    private apiLavandierService: ApiLavandierService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.search = this.search.bind(this);
    this.formatter = this.formatter.bind(this);
  }

  get articleFormArray(): FormArray {
    return this.articlePackForm.get('articleList') as FormArray;
  }

  ngOnInit() {
    this.apiLavandierService.getArticleList()
      .subscribe((articleList: any[]) => this.articleList = articleList);

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.articlePackId = Number(id);
      this.apiLavandierService.getArticlePack({})
        .subscribe(articlePackList => {
          const articlePack = articlePackList.find(ap => ap.id === this.articlePackId);
          if (!articlePack) {
            this.router.navigate(['/article-pack/list']);
            return;
          }
          this.articlePackForm.patchValue({
            name: articlePack.name,
            discount: articlePack.discount / PRICEMULTIPLIER,
            all: articlePack.all,
          });
          articlePack.articlePackArticleList.forEach(articlePackArticle => {
            this.addArticle(articlePackArticle.articleId, articlePackArticle.quantity / PRICEMULTIPLIER);
          });
          this.userList = articlePack.articlePackUserList.map(articlePackUser => articlePackUser.user);
        });
    } else {
      this.addArticle();
    }
  }

  addArticle(articleId: number = null, quantity: number = 1) {
    this.articleFormArray.push(this.fb.group({
      articleId: [articleId, Validators.required],
      quantity: [quantity, [Validators.required, Validators.min(1)]],
    }));
  }

  removeArticle(index: number) {
    this.articleFormArray.removeAt(index);
  }

  search(text: Observable<string>): Observable<IUser[]> {
    return text.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(inputData => {
        if (inputData === '') {
          return of([]);
        }
        const params = isNaN(Number(inputData)) ? {name: inputData} : {id: inputData};
        return this.apiLavandierService.getUserList(params)
          .pipe(map((data: any) => data.rows.filter(user => !this.userList.some(u => u.id === user.id))));
      })
    );
  }

  formatter(user: IUser) {
    return new TitleCasePipe().transform(user.id + ' ' + user.lastName + ' ' + user.firstName);
  }

  selectUser(event, input) {
    event.preventDefault();
    if (!this.userList.some(user => user.id === event.item.id)) {
      this.userList.push(event.item);
    }
    input.value = '';
  }

  removeUser(index: number) {
    this.userList.splice(index, 1);
  }

  onSubmit() {
    const params: IArticlePackPostReq = {
      name: this.articlePackForm.value.name,
      discount: Math.round(this.articlePackForm.value.discount * PRICEMULTIPLIER),
      all: this.articlePackForm.value.all,
      articleList: this.articlePackForm.value.articleList.map(article => ({
        articleId: Number(article.articleId),
        quantity: Math.round(article.quantity * PRICEMULTIPLIER),
      })),
      userIdList: this.articlePackForm.value.all ? [] : this.userList.map(user => user.id),
    };

    const request = this.articlePackId
      ? this.apiLavandierService.putArticlePackId(this.articlePackId, params)
      : this.apiLavandierService.postArticlePack(params);
    request.subscribe(() => this.router.navigate(['/article-pack/list']));
  }
}
